import { configureZodI18n, type Locale, resources } from '@fsd/shared';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import enZodTranslation from 'zod-i18n-map/locales/en/zod.json';
import esZodTranslation from 'zod-i18n-map/locales/es/zod.json';
// Import zod translations
import ptZodTranslation from 'zod-i18n-map/locales/pt/zod.json';
import { config } from '../config/env.js';

const availableLocales: Locale[] = ['pt-BR', 'en-US', 'es-ES'];
const defaultLocale: Locale = (
  availableLocales.includes(config.defaultLocale as Locale) ? config.defaultLocale : 'pt-BR'
) as Locale;
const supportedLocales: Locale[] = availableLocales;
const fallbackLocale: Locale = (
  availableLocales.includes(config.fallbackLocale as Locale) ? config.fallbackLocale : 'pt-BR'
) as Locale;

// Merge zod translations with existing resources
const resourcesWithZod = {
  'pt-BR': {
    ...resources['pt-BR'],
    zod: ptZodTranslation,
  },
  'en-US': {
    ...resources['en-US'],
    zod: enZodTranslation,
  },
  'es-ES': {
    ...resources['es-ES'],
    zod: esZodTranslation,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: resourcesWithZod,
    defaultNS: 'common',
    fallbackNS: 'common',
    fallbackLng: fallbackLocale,
    supportedLngs: supportedLocales,
    debug: false,
    returnNull: false,
    returnEmptyString: false,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'cookie', 'navigator', 'htmlTag', 'path', 'subdomain'],
      lookupCookie: 'locale',
      lookupLocalStorage: 'i18nextLng',
      caches: ['localStorage', 'cookie'],
      cookieOptions: {
        path: '/',
        sameSite: 'strict',
        maxAge: 30 * 24 * 60 * 60, // 30 days
      },
      convertDetectedLanguage: (lng: string) => {
        if (supportedLocales.includes(lng as Locale)) {
          return lng;
        }
        const languageCode = lng.split('-')[0];
        if (languageCode) {
          const match = supportedLocales.find((loc) =>
            loc.toLowerCase().startsWith(languageCode.toLowerCase())
          );
          if (match) return match;
        }
        return defaultLocale;
      },
    },
    react: {
      useSuspense: false,
    },
  });

// Configure Zod i18n with initial locale
configureZodI18n(i18n.language as Locale, {
  t: (key: string, options?: Record<string, unknown>) => i18n.t(key, options),
});

// Update Zod i18n whenever language changes
i18n.on('languageChanged', (lng) => {
  configureZodI18n(lng as Locale, {
    t: (key: string, options?: Record<string, unknown>) => i18n.t(key, options),
  });
});

export default i18n;
